import React, { useMemo, useCallback } from 'react';
import useDataSource from '../../hooks/useDataSource';
import { useDataFetch } from '../../hooks/useDataFetch';
import { useNavigate } from 'react-router-dom';
import LoadingDots from '../loading/LoadingDots';
import { VectorSource, defaultStyle, defaultText } from '@avinet/react-openlayers';
import { SourceIndexes } from '../../config/SourceConfig';
import LineString from 'ol/geom/LineString';
import WKT from 'ol/format/WKT';
import { getMediaUrl } from '../../utils/getMediaUrl';

import config from '../../config/kystverket';

import './RouteAfter.scss';

function getRtzUrl(routeNumber) {
  return config.adaptive4Url + 'api/kyv/routeinfo/v1/rtz/routes/' + routeNumber + '/download';
}

function getRouteAnchoragesUrl(routeNumber) {
  return config.adaptive4Url + 'api/kyv/routeinfo/v1/rtz/routes/' + routeNumber + '/anchorage';
}

const AnchorageAfter = ({ routeNumber }) => {
  const { loading: loadingAnchorage, items: itemsAnchorage } = useDataFetch(
    getRouteAnchoragesUrl(routeNumber)
  );
  const filter = useMemo(() => {
    if (itemsAnchorage && itemsAnchorage.length > 0) {
      const ids = itemsAnchorage.join(',');
      return `id IN (${ids})`;
    } else {
      return '';
    }
  }, [itemsAnchorage]);
  const { loading, items } = useDataSource({
    type: SourceIndexes.ANCHORAGE,
    filter,
    pause: !!loadingAnchorage
  });
  const hasData = useMemo(() => {
    return filter && !loading && items && items.length > 0;
  }, [filter, loading, items]);

  const navigate = useNavigate();

  const onClick = useCallback(item => navigate(config.themes.anchorage.url + item.id), [navigate]);

  return (
    <div>
      <h4>Anchorages for Sea Transport</h4>
      {(loading || loadingAnchorage) && <LoadingDots />}
      {hasData &&
        items.map(item => (
          <p key={`anchorage_${item.id}`} className="anchorage subList">
            <button
              className="btn btn-link"
              onMouseOver={() => item.olFeature.set('hover', true)}
              onMouseOut={() => item.olFeature.set('hover', false)}
              onClick={() => onClick(item)}
            >
              {item.anchoragename}
            </button>
          </p>
        ))}
      {!hasData && <p>None</p>}
    </div>
  );
};

const MtrAfter = ({ filter }) => {
  const { loading, items } = useDataSource({ type: SourceIndexes.MTR, filter });
  const navigate = useNavigate();

  const onClick = useCallback(item => navigate('/view/mtr/' + item.id), [navigate]);

  return (
    <div>
      <h4>Local Regulations (VTS)</h4>
      {loading && <LoadingDots />}
      {!loading && items && (
        <VectorSource
          layerName="route_mtr"
          features={items.map(item => item.olFeature) || []}
          selectable={false}
          useHoverStyle
          style={
            new defaultStyle({
              fillColor: 'rgba(255,255,255,0)',
              hoverColor: 'rgba(0,153,255, .3)',
              strokeColor: 'rgba(255,255,255,.5)',
              strokeWidth: 3,
              labelText: defaultText({
                labelProperty: 'section'
              })
            })
          }
        />
      )}
      {!loading &&
        items &&
        items.map(item => (
          <p key={`mtr_${item.id}`} className="mtr subList">
            <button
              className="btn btn-link"
              onMouseOver={() => item.olFeature.set('hover', true)}
              onMouseOut={() => item.olFeature.set('hover', false)}
              onClick={() => onClick(item)}
            >
              {item.section}
            </button>
          </p>
        ))}
      {!loading && items && items.length === 0 && <p>None</p>}
    </div>
  );
};

const VtsAfter = ({ filter }) => {
  const { loading, items } = useDataSource({ type: SourceIndexes.VTS, filter });

  return (
    <div>
      <h4>VTS information</h4>
      {loading && <LoadingDots />}
      {!loading &&
        items &&
        items.map(item => (
          <p key={`vts_${item.id}`}>
            {item.areaname_eng},{' '}
            <a
              href={getMediaUrl(item.factsheet, item.areaname_eng + '.pdf')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Fact sheet
            </a>
          </p>
        ))}
      {!loading && items && items.length === 0 && <p>None</p>}
    </div>
  );
};

const CprAfter = ({ filter }) => {
  const { loading, items } = useDataSource({ type: SourceIndexes.CPR, filter });
  const navigate = useNavigate();

  const onClick = useCallback(item => navigate('/view/cpr/' + item.id), [navigate]);

  return (
    <div>
      <h4>Local Regulations for Use of Pilot Exemption Certificate (PEC)</h4>
      {loading && <LoadingDots />}
      {!loading && (
        <VectorSource
          layerName="route_cpr"
          features={(items && items.map(item => item.olFeature)) || []}
          selectable={false}
          useHoverStyle
          style={
            new defaultStyle({
              fillColor: 'rgba(255,255,255,0)',
              hoverColor: 'rgba(0,153,255, .3)',
              strokeColor: 'rgba(255,255,255,.5)',
              strokeWidth: 3,
              labelText: defaultText({
                labelProperty: 'name'
              })
            })
          }
        />
      )}
      {!loading &&
        items &&
        items.map(item => (
          <p key={`cpr_${item.id}`} className="cpr subList">
            <button
              className="btn btn-link"
              onMouseOver={() => item.olFeature.set('hover', true)}
              onMouseOut={() => item.olFeature.set('hover', false)}
              onClick={() => onClick(item)}
            >{`${item.name} ${item.maritimedivisionname}`}</button>
          </p>
        ))}
      {!loading && (!items || items.length === 0) && '150 meter LOA'}
    </div>
  );
};

const SvalbardAfter = ({ filter }) => {
  const { loading, items } = useDataSource({ type: SourceIndexes.SVALBARD, filter });
  const navigate = useNavigate();

  const onClick = useCallback(item => navigate('/view/svalbard/' + item.id), [navigate]);

  return (
    <div>
      {items && items.length !== 0 ? (
        <div>
          <h4>Svalbard, Regulations and Information Services</h4>
          {loading && <LoadingDots />}
          {!loading && (
            <VectorSource
              layerName="route_svalbard"
              features={(items && items.map(item => item.olFeature)) || []}
              selectable={false}
              useHoverStyle
              style={
                new defaultStyle({
                  fillColor: 'rgba(255,255,255,0)',
                  hoverColor: 'rgba(0,153,255, .3)',
                  strokeColor: 'rgba(255,255,255,.5)',
                  strokeWidth: 3,
                  labelText: defaultText({
                    labelProperty: 'name'
                  })
                })
              }
            />
          )}
          {!loading &&
            items.map(item => (
              <p key={`svalbard_${item.id}`} className="subList">
                <button
                  className="btn btn-link"
                  onMouseOver={() => item.olFeature.set('hover', true)}
                  onMouseOut={() => item.olFeature.set('hover', false)}
                  onClick={() => onClick(item)}
                >{`${item.name}`}</button>
              </p>
            ))}
        </div>
      ) : null}
    </div>
  );
};

// const LfrAfter = ({ routeNumber }) => {
//   const { items, loading } = useDataFetch(getLfrOnRouteUrl(routeNumber));

//   const navigate = useNavigate();

//   const onClick = useCallback(item => navigate('/view/lfr/' + item.id), [navigate]);

//   if (loading) return <LoadingDots />;

//   return (
//     <div>
//       <h4>Pilot Guidelines (test, all guidelines might not be included)</h4>
//       <p>
//         <a
//           href="https://www.kystverket.no/en/news/pilot-guidelines-in-a-new-digital-solution/"
//           target="_blank"
//           rel="noopener noreferrer"
//         >
//           Guidelines for voyages with pilotage
//         </a>
//         .
//       </p>
//       {items && items.length !== 0 ? (
//         items.map(item => (
//           <p key={`lfr_${item.id} `} className="lfr subList">
//             <button
//               className="btn btn-link"
//               onClick={() => onClick(item)}
//             >{`${item.title}`}</button>
//           </p>
//         ))
//       ) : (
//         <p>No relevant pilot guidelines found for this item.</p>
//       )}
//     </div>
//   );
// };

const RouteAfter = ({ id, routeName, routeNumber, routeLine, autoLength }) => {
  // Shortening LineString to make get request accept the length of it
  const simplifiedRouteWkt = useMemo(() => {
    if (!routeLine) return null;

    const wktFormat = new WKT();

    // Parse WKT string into a LineString geometry
    const geometry = wktFormat.readGeometry(routeLine, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857'
    });

    // Simplify with 50m tolerance
    const simplifiedGeometry = geometry.simplify(50);

    // Round to integers to drop decimals
    const coords = simplifiedGeometry
      .getCoordinates()
      .map(([x, y]) => [Math.round(x), Math.round(y)]);

    // Rebuild LineString and return WKT
    const finalGeometry = new LineString(coords);
    return wktFormat.writeGeometry(finalGeometry);
  }, [routeLine]);

  const filter = useMemo(() => {
    return `S_INTERSECTS(geom, ${simplifiedRouteWkt})`;
  }, [simplifiedRouteWkt]);

  return (
    <div className="route-after--root">
      <div className="additional">
        <h5>Routename</h5>
        <p>{routeName}</p>
      </div>
      {id && (
        <p>
          {id && (
            <a href={getRtzUrl(routeNumber)} download={routeName} className="btn btn-info">
              Download RTZ 1.0
            </a>
          )}
        </p>
      )}
      <h3>Route Info</h3>
      <SvalbardAfter filter={filter} />
      <div className="additional">
        <h5>Sailing Distance</h5>
        <p>{(autoLength / 1852).toFixed(1)} nautical miles</p>
      </div>
      <CprAfter filter={filter} />
      <MtrAfter filter={filter} />
      <VtsAfter filter={filter} />
      <AnchorageAfter routeNumber={routeNumber} />
      {/* <LfrAfter routeNumber={routeNumber} /> */}
    </div>
  );
};

export default RouteAfter;
